import { API_ROUTES } from "../constants/api-routes";
import {
  Account,
  AuthResponse,
  ContactResponse,
  DisabledConversation,
  DisabledConversations,
  MessageData,
  MessageType,
  RefreshTokenResponse,
} from "../interfaces/all";
import { apiService } from "./axios";

// Tipos para dados de autenticação
interface LoginData {
  identifier: string;
  password: string;
}

interface SendTemplateMessageData {
  to: string;
  templateId: string;
  templateName: string;
  templateLanguage: string;
  parameters?: string[];
  userId: string;
  companyId: string;
  imageUrl?: string;
  leadId?: number;
  leadSource?: string;
  campaignId?: number;
}

// Tipos para dados de upload
interface UploadFileData {
  file: File;
  type: "image" | "document" | "audio";
}

// Funções de autenticação
export const authLogin = async (identifier: string, password: string) => {
  const data: LoginData = { identifier, password };
  return apiService.post<AuthResponse>(API_ROUTES.AUTH.LOGIN, data);
};

export const authLogout = async () => {
  return apiService.post<{ success: boolean }>(API_ROUTES.AUTH.LOGOUT);
};

export const authRefreshToken = async () => {
  return apiService.post<RefreshTokenResponse>(API_ROUTES.AUTH.REFRESH_TOKEN);
};

export const getAccount = async () => {
  return apiService.get<AuthResponse>(API_ROUTES.AUTH.ME);
};

export const getSsoToken = async () => {
  return apiService.get<{ sso_token: string }>("/auth/sso-token");
};

// Funções de dados da empresa
export const createCompany = async (companyData: any) => {
  return apiService.post<{
    status: "success";
    data: Account["company"];
    message: string;
  }>(API_ROUTES.COMPANIES.GET_ALL, companyData);
};

export const getCompanyData = async (companyId: string) => {
  return apiService.get<{ data: Account["company"] }>(
    API_ROUTES.COMPANIES.GET(companyId),
  );
};

/** Retorna o domain do CRM (PostgreSQL companies_company) para construir o link do lead. */
export const getCrmDomain = async (companyId: string) => {
  return apiService.get<{ domain: string | null }>(
    API_ROUTES.COMPANIES.GET_CRM_DOMAIN(companyId),
  );
};

/** Consulta o limite de mensagens na Meta e atualiza meta.limitation (Limite informado pela Meta + última verificação). */
export const refreshMetaLimits = async (companyId: string) => {
  return apiService.post<{
    data?: {
      limitation: {
        messaging_limit_tier_from_meta?: string;
        last_checked: string;
      };
    };
    message?: string;
  }>(API_ROUTES.COMPANIES.REFRESH_META_LIMITS(companyId));
};

/** Define "Mensagens restantes (hoje)" igual ao teto da Meta. Requer ter consultado o limite da Meta antes. */
export const setRemainingToMetaTier = async (companyId: string) => {
  return apiService.post<{
    data?: { max_messages_per_day: string };
    message?: string;
  }>(API_ROUTES.COMPANIES.SET_REMAINING_TO_META_TIER(companyId));
};

// Funções de contatos
export const getContacts = async (cpf: string, page = 1, limit = 50) => {
  return apiService.get<ContactResponse>(API_ROUTES.CONTACTS.BASE, {
    params: { cpf, page, limit },
  });
};

// Funções de mensagens
export const getMessagesByClientPhone = async (
  phone: string,
  page = 1,
  limit = 50,
) => {
  // Validar e limpar o número de telefone
  let cleanPhone = phone?.trim().replace(/[^\d]/g, "") || "";

  if (!cleanPhone) {
    throw new Error("Número de telefone inválido ou vazio");
  }

  // Garantir que tenha o código do país (55 para Brasil)
  if (!cleanPhone.startsWith("55")) {
    if (cleanPhone.length === 11) {
      // Número brasileiro sem código do país
      cleanPhone = "55" + cleanPhone;
    } else if (cleanPhone.length === 10) {
      // Número brasileiro sem código do país e sem DDD
      cleanPhone = "55" + cleanPhone;
    }
  }

  const response = await apiService.get<{
    status: "success";
    data: {
      messages: MessageType[];
      total: number;
      page: number;
      limit: number;
      hasMore: boolean;
    };
  }>(API_ROUTES.MESSAGES.BASE, {
    params: {
      clientPhone: cleanPhone,
      page,
      limit,
    },
  });
  return response;
};

export async function newMessages(data: MessageData | FormData) {
  return apiService.post<{ data: MessageType }>(API_ROUTES.MESSAGES.SEND, data);
}

export const sendTemplateMessage = async (
  templateData: SendTemplateMessageData,
) => {
  return apiService.post<{ data: MessageType }>(
    API_ROUTES.MESSAGES.TEMPLATE,
    templateData,
  );
};

// Funções de upload
export const uploadFile = async (fileData: UploadFileData) => {
  const formData = new FormData();
  formData.append("file", fileData.file);
  formData.append("type", fileData.type);

  return apiService.post<{ url: string; filename: string }>(
    API_ROUTES.FILE.UPLOAD,
    formData,
    {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    },
  );
};

export const getDisabledConversations = async (page = 1, limit = 50) => {
  return apiService.get<DisabledConversations>(
    API_ROUTES.MESSAGES.AI_DISABLED_CONVERSATIONS,
    {
      params: { page, limit },
    },
  );
};

export const toggleAIState = async (
  phoneNumber: string,
  isActive: boolean,
  reason?: string,
) => {
  return apiService.post<{ status: "success"; data: DisabledConversation }>(
    API_ROUTES.MESSAGES.AI_TOGGLE(phoneNumber),
    { isActive, reason },
  );
};

// Funções de templates
export const getTemplates = async (companyId: string, page = 1, limit = 50) => {
  return apiService.get<{ data: Array<Record<string, unknown>> }>(
    API_ROUTES.MESSAGES.TEMPLATES,
    {
      params: { companyId, page, limit },
      timeout: 60000, // 60 segundos para carregar templates
    },
  );
};

export const getTemplatesForUserCampaigns = async () => {
  return apiService.get<{
    status: "success";
    data: Array<Record<string, unknown>>;
  }>(API_ROUTES.MESSAGES.CAMPAIGN_TEMPLATES.GET_USER, {
    timeout: 60000, // 60 segundos para carregar templates
  });
};

export const assignTemplateToCampaign = async (assignmentData: {
  campaignId: string;
  campaignName?: string;
  templateId: string;
  templateName?: string;
  templateStatus?: string;
  templateCategory?: string;
  templateLanguage?: string;
  assignedBy?: string;
}) => {
  return apiService.post<{ status: "success"; data: Record<string, unknown> }>(
    API_ROUTES.MESSAGES.CAMPAIGN_TEMPLATES.ASSIGN,
    assignmentData,
  );
};

export const removeTemplateFromCampaign = async (
  campaignId: string,
  templateId: string,
) => {
  return apiService.delete<{
    status: "success";
    data: Record<string, unknown>;
  }>(API_ROUTES.MESSAGES.CAMPAIGN_TEMPLATES.REMOVE(campaignId, templateId));
};

// Funções de campanhas
export const getAllCampaigns = async () => {
  return apiService.get<{
    status: "success";
    data: {
      campaigns: Array<Record<string, unknown>>;
      total: number;
      page: number;
      limit: number;
      hasMore: boolean;
      totalPages: number;
    };
  }>(API_ROUTES.CONTACTS.GET_CAMPAIGNS);
};

export const getCampaignsWithTemplates = async () => {
  return apiService.get<{
    status: "success";
    data: Array<Record<string, unknown>>;
  }>(API_ROUTES.MESSAGES.CAMPAIGN_TEMPLATES.GET_ALL);
};

// Funções de envio em massa
export const sendBulkTemplate = async (bulkData: {
  campaignId: string;
  templateId: string;
  templateName: string;
  parameters: string[];
  sentBy: string;
}) => {
  return apiService.post<{ status: "success"; data: Record<string, unknown> }>(
    API_ROUTES.MESSAGES.BULK_TEMPLATE,
    bulkData,
  );
};

// Função para buscar informações sobre parâmetros necessários do template
export const getTemplateParameters = async (
  templateId: string,
  templateName: string,
  companyId?: string,
) => {
  const params = new URLSearchParams();
  if (templateName) params.append("name", templateName);
  if (companyId) params.append("companyId", companyId);

  return apiService.get(
    `${API_ROUTES.MESSAGES.TEMPLATE_PARAMETERS(templateId)}?${params.toString()}`,
  );
};

// Funções de status de leitura de mensagens
export const markMessageAsRead = async (
  messageId: string,
  userId: string,
  userName: string,
) => {
  return apiService.patch<{ success: boolean }>(
    API_ROUTES.MESSAGES.MARK_AS_READ(messageId),
    {
      userId,
      userName,
      readAt: new Date().toISOString(),
    },
  );
};

export const markMessageAsUnread = async (messageId: string) => {
  return apiService.patch<{ success: boolean }>(
    API_ROUTES.MESSAGES.MARK_AS_UNREAD(messageId),
  );
};

export const markAllMessagesAsRead = async (
  phoneNumber: string,
  userId: string,
  userName: string,
) => {
  return apiService.patch<{ success: boolean }>(
    API_ROUTES.MESSAGES.MARK_ALL_AS_READ(phoneNumber),
    {
      userId,
      userName,
      readAt: new Date().toISOString(),
    },
  );
};

export const sendVoiceCallButton = async (
  companyId: string,
  to: string,
  options?: { bodyText?: string; displayText?: string; ttlMinutes?: number },
) => {
  return apiService.post<{
    status: "success";
    data: Record<string, unknown>;
    message: string;
  }>(API_ROUTES.MESSAGES.CALL_BUTTON, {
    companyId,
    to,
    ...options,
  });
};

export const startWebCall = async (payload: {
  companyId: string;
  to: string;
  sdpOffer: string;
  socketId: string;
}) => {
  return apiService.post<{
    status: "success";
    data: { callId: string };
    message: string;
  }>(API_ROUTES.MESSAGES.CALLS_START, payload);
};

export const terminateWebCall = async (payload: {
  companyId: string;
  callId: string;
}) => {
  return apiService.post<{
    status: "success";
    data: Record<string, unknown>;
    message: string;
  }>(API_ROUTES.MESSAGES.CALLS_TERMINATE, payload);
};

export const getUnreadMessagesCount = async (phoneNumber: string) => {
  return apiService.get<{ status: "success"; data: number }>(
    API_ROUTES.MESSAGES.UNREAD_COUNT(phoneNumber),
  );
};

// Funções de informações do WhatsApp
export const fetchWhatsAppInfo = async (companyId: string) => {
  return apiService.patch<{ status: "success"; data: Record<string, unknown> }>(
    API_ROUTES.COMPANIES.FETCH_WHATSAPP_INFO(companyId),
  );
};

export const updateWabaId = async (companyId: string, wabaId: string) => {
  return apiService.put<{ status: "success"; message: string }>(
    API_ROUTES.COMPANIES.WABA_ID(companyId),
    { wabaId },
  );
};

export const updatePhoneNumberId = async (
  companyId: string,
  phoneNumberId: string,
) => {
  return apiService.put<{ status: "success"; message: string }>(
    API_ROUTES.COMPANIES.PHONE_NUMBER_ID(companyId),
    { phoneNumberId },
  );
};

export const requestPhoneVerificationCode = async (
  companyId: string,
  codeMethod: "SMS" | "VOICE" = "SMS",
) => {
  return apiService.post<{
    status: "success";
    data: Record<string, unknown>;
    message: string;
  }>(API_ROUTES.COMPANIES.PHONE_VERIFICATION_REQUEST_CODE(companyId), {
    codeMethod,
  });
};

export const verifyPhoneVerificationCode = async (
  companyId: string,
  code: string,
) => {
  return apiService.post<{
    status: "success";
    data: Record<string, unknown>;
    message: string;
  }>(API_ROUTES.COMPANIES.PHONE_VERIFICATION_VERIFY_CODE(companyId), { code });
};

/** Atualiza campos de configuração do app em meta.* (app_version, access_token, verify_token, app_identify). */
export const updateMetaAppConfig = async (
  companyId: string,
  config: {
    app_version?: string;
    access_token?: string;
    verify_token?: string;
    app_identify?: string;
  },
) => {
  return apiService.put<{ status: "success"; message: string }>(
    API_ROUTES.COMPANIES.META_APP_CONFIG(companyId),
    config,
  );
};

/** Atualiza o perfil de negócio na Meta (about, address, description, email, vertical, websites). */
export const updateBusinessProfile = async (
  companyId: string,
  payload: {
    about?: string;
    address?: string;
    description?: string;
    email?: string;
    vertical?: string;
    websites?: string[];
    profile_picture_handle?: string;
  },
) => {
  return apiService.patch<{
    status: "success";
    data?: { whatsapp_profile: unknown };
    message?: string;
  }>(API_ROUTES.COMPANIES.BUSINESS_PROFILE(companyId), payload);
};

/** Apaga o histórico de mensagens de uma conversa. */
export const deleteConversationHistory = async (conversationId: string) => {
  return apiService.delete<{ success: boolean; message?: string }>(
    `/api/crm/messages/${conversationId}`,
  );
};
